<template>
  <div
    v-if="
      currentMission != null &&
      currentMission.typeName === 'ConstellationPerformanceAnalysisRequest'
    "
  >
    <div class="text-xs-center" v-if="displayComputationWidget">
      <exo-mission-computation
        gt_PerformanceAnalysis-v-step="53"
      ></exo-mission-computation>
    </div>
    <div v-if="displayResult" m-0 p-0>
      <div v-if="loading">Loading data and map ...</div>
      <v-card v-if="!loading">
        <v-row align-center justify-center style="background: #1e1e1e">
          <v-col cols="7">
            <span class="ml-4 headline grey--text">
              {{ currentMission.name.toUpperCase() }} -
              {{
                getResultsFieldLabel(
                  appliedDashboardConfiguration.currentCategory
                )
              }}
              /
              {{
                getResultsFieldLabel(appliedDashboardConfiguration.currentMap)
              }}
            </span>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="5" m-2 class="text-right">
            <v-dialog v-if="displayError" v-model="dialog" width="700">
              <template v-slot:activator="{ on: dialog }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      large
                      icon
                      class="bug-button"
                      v-on="{ ...dialog, ...tooltip }"
                    >
                      <icon
                        class="white--text"
                        scale="1.5"
                        name="user-injured"
                      />
                    </v-btn>
                  </template>
                  <span>Bug report</span>
                </v-tooltip>
              </template>
              <error-log-display
                :root="_self"
                :computationLog="currentResultsLog"
              ></error-log-display>
            </v-dialog>
            <v-tooltip
              bottom
              class="caption justify-center warning--text"
              v-if="currentMission.dirty"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  class="white--text"
                  v-on="on"
                  @click.stop="requestComputation()"
                  color="warning"
                  >Start Recompute</v-btn
                >
              </template>
              <span
                >You have changed some components of the mission. This results
                does not match the current inputs. Please recompute the mission
                to update.</span
              >
            </v-tooltip>
            <v-bottom-sheet inset v-if="displayWarning">
              <template v-slot:activator="{ on: bottomSheet }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      large
                      icon
                      color="warning"
                      v-on="{ ...tooltip, ...bottomSheet }"
                    >
                      <icon
                        scale="1.5"
                        class="warning--text"
                        name="exclamation"
                      ></icon>
                    </v-btn>
                  </template>
                  <span>Alert message</span>
                </v-tooltip>
              </template>
              <v-card>
                <div class="alert-div"></div>
                <v-alert
                  outlined
                  :value="true"
                  type="warning"
                  class="m-0"
                  v-if="emptyResult"
                  >Your constellation is blind during this period, you should
                  reconsider the configuration or select another
                  result.</v-alert
                >
                <v-alert
                  outlined
                  :value="true"
                  type="warning"
                  class="m-0"
                  v-if="displaySpatialAverageWarning"
                >
                  <p>
                    The metrics were computed for a mesh with constant latitude
                    and longitude steps. This results in a greater concentration
                    of points at higher latitudes. The Statistics and the Global
                    histograms are computed based on the mesh points, which are
                    not uniformly distributed on the Earth surface.
                    Consequently, they artificially give more importance to the
                    results around higher latitudes.
                  </p>

                  <i>Nota Bene: </i> to obtain a mesh that represents the Earth
                  surface at higher latitudes, you may set the 'Mesh element
                  definition' to 'Constant area' in the Earth Mesh form.
                </v-alert>
              </v-card>
            </v-bottom-sheet>

            <v-menu
              transition="slide-y-transition"
              nudge-bottom="15"
              offset-y
              bottom
              min-width="600"
              max-height="600"
              v-if="dataLoaded"
            >
              <template v-slot:activator="{ on }">
                <v-btn color="primary" dark v-on="on">
                  Export
                  <v-icon dark>arrow_drop_down</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="exportDialog = true">
                  <v-list-item-title :color="$vuetify.theme.dark ? '' : 'black'"
                    >Map data</v-list-item-title
                  >
                </v-list-item>
                <v-list-item
                  v-if="groundStationEphemeridesReady"
                  @click.stop="exportGroundStationEphemerides()"
                >
                  <v-list-item-title :color="$vuetify.theme.dark ? '' : 'black'"
                    >Ground stations ephemerides</v-list-item-title
                  >
                </v-list-item>
                <v-list-item
                  v-if="revisitEphemeridesReady"
                  @click.stop="exportRevisitEphemerides()"
                >
                  <v-list-item-title :color="$vuetify.theme.dark ? '' : 'black'"
                    >Revisit ephemerides</v-list-item-title
                  >
                </v-list-item>
                <v-list-item
                  v-if="
                    appliedDashboardConfiguration.displayHistogramView === true
                  "
                  @click.stop="exportStatisticsHistogram()"
                >
                  <v-list-item-title :color="$vuetify.theme.dark ? '' : 'black'"
                    >Statistics histogram</v-list-item-title
                  >
                </v-list-item>
                <v-list-item
                  v-if="
                    appliedDashboardConfiguration.displayGlobalHistogramView ===
                    true
                  "
                  @click.stop="exportGlobalHistogram()"
                >
                  <v-list-item-title :color="$vuetify.theme.dark ? '' : 'black'"
                    >Global histogram</v-list-item-title
                  >
                </v-list-item>
                <v-list-item
                  v-if="
                    appliedDashboardConfiguration.displayLocalHistogramView ===
                    true
                  "
                  @click.stop="exportLocalHistogram()"
                >
                  <v-list-item-title :color="$vuetify.theme.dark ? '' : 'black'"
                    >Local histogram</v-list-item-title
                  >
                </v-list-item>
              </v-list>
            </v-menu>

            <v-tooltip bottom v-if="dataLoaded">
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click="openDashboardSettings"
                  gt_PerformanceAnalysis-v-step="57"
                >
                  <icon scale="1.5" class="primary--text" name="cogs" />
                </v-btn>
              </template>
              <span>Dashboard settings</span>
            </v-tooltip>

            <v-tooltip bottom v-if="dataLoaded">
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="saveCurrentLayout()"
                >
                  <icon scale="1.5" class="primary--text" name="save"></icon>
                </v-btn>
              </template>
              <span
                >Save the layout and the configurations of the dashboard</span
              >
            </v-tooltip>
            <v-tooltip bottom v-if="dataLoaded">
              <template v-slot:activator="{ on }">
                <v-btn
                  large
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="resetLayout()"
                >
                  <icon
                    scale="1.5"
                    class="primary--text"
                    name="undo-alt"
                  ></icon>
                </v-btn>
              </template>
              <span
                >Reset the layout and the configurations of the dashboard</span
              >
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row no-gutters class="ml-4">
          <v-select
            class="mr-2"
            :items="mapCategories"
            v-model="editedDashboardConfiguration.currentCategory"
            label="Map Category"
            v-on:input="categoryUpdated"
            gt_PerformanceAnalysis-v-step="54"
          >
            <template slot="selection" slot-scope="data">{{
              getResultsFieldLabel(data.item)
            }}</template>
            <template slot="item" slot-scope="data">{{
              getResultsFieldLabel(data.item)
            }}</template>
          </v-select>
          <exo-help-dialog
            icon-scale="1.5"
            title="Collect time"
            v-if="
              editedDashboardConfiguration.currentCategory == 'pointCollectTime'
            "
          >
            <tooltipCollectTime />
          </exo-help-dialog>
          <v-select
            class="mr-2"
            :items="availableMapTypes"
            v-model="editedDashboardConfiguration.currentMap"
            label="Map type"
            gt_PerformanceAnalysis-v-step="55"
          >
            <template slot="selection" slot-scope="data">{{
              getResultsFieldLabel(data.item)
            }}</template>
            <template slot="item" slot-scope="data">{{
              getResultsFieldLabel(data.item)
            }}</template>
          </v-select>
          <exo-help-dialog icon-scale="1.5" title="Results Description">
            <tooltipRevisitGroundSegments />
          </exo-help-dialog>
          <v-card-actions class="mx-4 my-3">
            <v-btn
              @click="updateMap()"
              id="updateMapBtn"
              color="primary"
              gt_PerformanceAnalysis-v-step="56"
              >Update map</v-btn
            >
          </v-card-actions>
          <div gt_PerformanceAnalysis-v-step="65" />
        </v-row>
        <v-row
          no-gutters
          class="ml-4"
          v-if="
            editedDashboardConfiguration.currentCategory ===
            'pointNumberOfAccesses'
          "
        >
          <exo-form-alert class="mb-2" type="info">
            The metric 'Number of accesses' does not support local and global
            histograms, since it does not represent a list of instances through
            time, but rather one sole value for each point.
          </exo-form-alert>
        </v-row>
        <v-row
          no-gutters
          class="ml-4"
          v-if="
            editedDashboardConfiguration.currentMap ===
            'pointAccessDurationPercentile'
          "
        >
          <exo-form-alert class="mb-2" type="info">
            The access duration percentiles values are inverted. If the p-th
            percentile is equal to X, p% of the revisits on that point have a
            duration above or equal X.
          </exo-form-alert>
        </v-row>
        <v-row
          no-gutters
          class="ml-4"
          v-if="
            editedDashboardConfiguration.currentMap ===
            'pointSimultaneousSpacecraftsPercentile'
          "
        >
          <exo-form-alert class="mb-2" type="info">
            The 'number of spacecrafts simultaneously seen' percentiles values
            are inverted. If the p-th percentile is equal to X, then that point
            sees at least X spacecrafts on p% of the simulation duration.
          </exo-form-alert>
        </v-row>
        <v-row
          no-gutters
          v-if="
            editedDashboardConfiguration.currentMap.includes('Percentile') ===
            true
          "
        >
          <v-col class="px-3" cols="5">
            <v-slider
              v-model="editedDashboardConfiguration.currentPercentileIndex"
              label="Percentile"
              color="rgb(51, 204, 255)"
              :tick-labels="currentResultsLog.results.percentilesValues"
              :max="
                currentResultsLog.results.percentilesValues == null
                  ? 4
                  : currentResultsLog.results.percentilesValues.length - 1
              "
              ticks="always"
            >
            </v-slider>
          </v-col>
        </v-row>
      </v-card>

      <div v-if="buildingMap">Building map ...</div>
      <v-row no-gutters v-if="dataLoaded" class="mt-2 px-2 align-center">
        <v-col
          :cols="appliedDashboardConfiguration.displayHeatmapView ? 8 : 12"
          class="pa-2"
          v-if="appliedDashboardConfiguration.display3DView === true"
        >
          <v-card outlined>
            <v-card-title>{{
              appliedMetricName +
              (availableMeshOptions.length > 1 ? ' on all meshes' : '')
            }}</v-card-title>
            <v-card-text>
              <dashboard-viewer
                :currentData="currentData"
                :resultsLog="currentResultsLog"
                :heatMapCommons="heatMapCommons"
                :displaySinglePointResults="displaySinglePointResults"
                :displayGroundStationTimelineFrom3DView="
                  displayGroundStationTimelineFrom3DView
                "
                gt_PerformanceAnalysis-v-step="62"
              />
            </v-card-text>
          </v-card>
          <div gt_PerformanceAnalysis-v-step="63" />
        </v-col>

        <v-col
          cols="4"
          class="pa-2"
          v-if="appliedDashboardConfiguration.displayHeatmapView === true"
        >
          <v-card outlined>
            <v-card-title>
              {{ appliedMetricName }}
              <v-spacer></v-spacer>
              <v-select
                class="mr-2"
                v-on:input="meshIndexUpdated"
                :items="availableMeshOptions"
                v-model="heatmapMeshIndex"
                label="Displayed mesh"
                v-if="availableMeshOptions.length > 1"
              >
                <template slot="selection" slot-scope="data">{{
                  getMeshName(data.item)
                }}</template>
                <template slot="item" slot-scope="data">{{
                  getMeshName(data.item)
                }}</template>
              </v-select>
            </v-card-title>
            <div
              :key="updateIndex"
              :id="idWidgetHeatmapView"
              :ref="idWidgetHeatmapView"
            ></div>
            <exo-form-alert
              type="warning"
              class="ml-4 mr-2"
              v-if="heatMapHasUndefinedLocation"
            >
              Warning: If a location has only one revisit, the average gap
              duration can not be computed. It is then set as "undefined" and
              displayed in black on the heatmap.
            </exo-form-alert>
          </v-card>
        </v-col>
        <v-col
          cols="6"
          class="pa-2"
          v-if="appliedDashboardConfiguration.displayTimelines === true"
        >
          <v-card outlined grow fill-height>
            <exo-form-alert type="info" class="ml-4 mr-2" v-if="dataReady"
              >To display the single point results (revisit timeline and local
              histogram) of any point of the map, choose the point coordinates
              below. The point can be out of the initial meshing. No need to
              have computed revisits before. Alternatively, click anywhere on
              the map above.
            </exo-form-alert>
            <v-row no-gutters class="px-6 pt-3" v-if="dataReady === true">
              <v-text-field
                dense
                class="px-2"
                v-model="singlePointLatitude"
                label="Latitude (deg)"
                :rules="[inputLatitudeFeedback]"
                gt_PerformanceAnalysis-v-step="64"
              ></v-text-field>
              <v-text-field
                dense
                class="px-2"
                v-model="singlePointLongitude"
                label="Longitude (deg)"
                :rules="[inputLongitudeFeedback]"
              ></v-text-field>
              <v-card-actions class="">
                <v-btn
                  @click="displaySinglePointResultsFromLatLon()"
                  id="displaySinglePointResultsFromLatLonBtn"
                  color="primary"
                  :disabled="validCoordinatesInputs === false"
                  >Update coordinates</v-btn
                >
              </v-card-actions>
            </v-row>
            <v-select
              class="ml-4 mr-2"
              :items="availableRevisitTimelines"
              :label="revisitTimelineCategory"
              v-on:input="updateRevisitTimeline"
              v-model="currentRevisitTimelineLabel"
              v-if="currentRevisitTimelineLabel != null"
              :menu-props="{ transition: 'scale-transition' }"
            ></v-select>
            <div class="ml-3 mr-4" id="revisitTimeline"></div>

            <exo-form-alert
              type="info"
              v-if="groundStationTimelineAvailable && dataReady"
              class="ml-4 mr-2"
            >
              To display a ground station visibility timeline, select a ground
              station from the list below. The extended ground segment is
              required. Alternatively, click with the middle button on a ground
              station or its field of view on the 3D map.
            </exo-form-alert>
            <v-select
              class="ml-4 mr-2"
              :items="groundStationNames"
              label="Ground station"
              v-on:input="displayGroundStationTimelineFromCache"
              v-model="currentGroundStationName"
              v-if="groundStationTimelineAvailable && dataReady"
              :menu-props="{ transition: 'scale-transition' }"
            ></v-select>

            <v-select
              class="ml-4 mr-2"
              :items="availableGroundStationTimelines"
              :label="groundStationTimelineCategory"
              v-on:input="updateGroundStationTimeline"
              v-model="currentGroundStationTimelineLabel"
              v-if="currentGroundStationTimelineLabel != null"
              :menu-props="{ transition: 'scale-transition' }"
            ></v-select>
            <div class="ml-3 mr-4" id="groundStationTimeline"></div>
          </v-card>
        </v-col>

        <v-col
          cols="6"
          class="pa-2"
          v-if="
            appliedDashboardConfiguration.displayLocalHistogramView === true
          "
        >
          <v-card outlined>
            <div v-if="loadingSinglePointResults === true" class="text-center">
              Computing local histogram...
            </div>
            <div
              v-if="
                loadingSinglePointResults === false &&
                localHistogramData.length == 0 &&
                dataReady === true
              "
              class="text-center"
            >
              No data for the metric '{{
                getResultsFieldLabel(
                  appliedDashboardConfiguration.currentCategory
                ).toLowerCase()
              }}' found for latitude {{ singlePointLatStr }} / longitude
              {{ singlePointLonStr }}
            </div>
            <div
              :id="idWidgetLocalHistogramView"
              :ref="idWidgetLocalHistogramView"
            ></div>
          </v-card>
        </v-col>

        <v-col
          cols="6"
          class="pa-2"
          v-if="appliedDashboardConfiguration.displayHistogramView === true"
        >
          <v-card outlined>
            <v-card-title>
              <v-select
                class="mr-2"
                v-on:input="meshIndexUpdated"
                :items="availableMeshOptions.concat(allMeshesIndex)"
                v-model="statisticsHistogramMeshIndex"
                label="Displayed mesh"
                v-if="availableMeshOptions.length > 1"
              >
                <template slot="selection" slot-scope="data">{{
                  getMeshName(data.item)
                }}</template>
                <template slot="item" slot-scope="data">{{
                  getMeshName(data.item)
                }}</template>
              </v-select>
            </v-card-title>
            <div
              :key="updateIndex"
              :id="idWidgetHistogramView"
              :ref="idWidgetHistogramView"
            ></div>
            <v-card-actions>
              <v-row
                no-gutters
                class="px-6"
                v-if="displayHistogramOptions === true && dataReady === true"
              >
                <v-text-field
                  dense
                  class="px-2"
                  v-model="editedDashboardConfiguration.histogramBinSize"
                  :label="
                    'Size of bins in x axis' +
                    (this.appliedDashboardConfiguration.currentCategory ==
                    'pointNumberOfAccesses'
                      ? ''
                      : ' (s)')
                  "
                  :rules="[inputBinSizeFeedback]"
                  @change="lastHistogramOptionUpdated = 'Bin size'"
                ></v-text-field>
                <v-text-field
                  dense
                  class="px-2"
                  v-model="editedDashboardConfiguration.histogramNumberOfBins"
                  label="Number of bins in x axis"
                  :rules="[inputBinNumberFeedback]"
                  @change="lastHistogramOptionUpdated = 'Number of bins'"
                ></v-text-field>
                <v-card-actions class="">
                  <v-btn
                    @click="updateHistogram()"
                    id="updateHistogramBtn"
                    color="primary"
                    :disabled="validHistogramInputs === false"
                    >Update histogram</v-btn
                  >
                </v-card-actions>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col
          cols="6"
          class="pa-2"
          v-if="
            appliedDashboardConfiguration.displayGlobalHistogramView === true
          "
        >
          <v-card outlined>
            <div
              :key="updateIndex"
              :id="idWidgetGlobalHistogramView"
              :ref="idWidgetGlobalHistogramView"
            ></div>
          </v-card>
        </v-col>
      </v-row>
      <v-dialog v-model="exportDialog" width="500">
        <exo-export-constellation-dialog
          :dashboard="_self"
        ></exo-export-constellation-dialog>
      </v-dialog>

      <v-dialog
        v-model="settingsDialog"
        width="800"
        :persistent="this.isGuidedTutorialInDisplaySettings"
      >
        <v-card v-if="this.settingsDialog === true">
          <!-- very important to disable if not displayed : the slider component would update zbound otherwize -->
          <v-card-title class="pb-0">
            <v-col>
              <v-row align="center">
                <icon scale="1.5" class="blue--text lighten-4" name="cogs" />
                <v-toolbar-title class="grey--text title"
                  >Display settings</v-toolbar-title
                >
              </v-row>
            </v-col>
          </v-card-title>
          <v-card-text>
            <exo-card-form
              label="Data filter"
              gt_PerformanceAnalysis-v-step="58"
            >
              <v-range-slider
                class="m-0 p-0 mt-4"
                v-model="editedDashboardConfiguration.zBounds"
                :min="minZInConfiguration"
                :max="maxZInConfiguration"
                :step="stepData"
                :label="
                  'Range: [' +
                  minZInConfiguration +
                  ',' +
                  maxZInConfiguration +
                  '] ' +
                  this.unitTypes[this.editedDashboardConfiguration.currentMap]
                "
                color="rgb(51, 204, 255)"
                thumb-label="always"
              >
                <template v-slot:thumb-label="item">
                  <span style="color: black">{{ item.value }}</span>
                </template>
              </v-range-slider>
            </exo-card-form>
            <exo-card-form label="Maps" gt_PerformanceAnalysis-v-step="59">
              <v-row no-gutters>
                <v-checkbox
                  v-model="editedDashboardConfiguration.display3DView"
                  class="my-0 py-0 mr-4"
                  label="3D view"
                ></v-checkbox>
                <v-checkbox
                  v-model="editedDashboardConfiguration.displayHeatmapView"
                  class="my-0 py-0 mr-4"
                  label="Heatmap"
                ></v-checkbox>
              </v-row>
              <v-row no-gutters>
                <v-select
                  :items="colorsMaps"
                  v-model="editedDashboardConfiguration.currentColorMap"
                  label="Map colorscale"
                ></v-select>
              </v-row>
            </exo-card-form>

            <exo-card-form
              label="Timelines and statistics"
              gt_PerformanceAnalysis-v-step="60"
            >
              <template v-slot:titleTooltip>
                <exo-help-dialog title="Histograms" :margin="'m-0'">
                  <tooltipHistograms />
                </exo-help-dialog>
              </template>
              <v-row no-gutters
                ><v-checkbox
                  v-model="editedDashboardConfiguration.displayTimelines"
                  class="my-0 py-0 mr-4"
                  label="Timelines"
                ></v-checkbox>
                <v-checkbox
                  v-model="
                    editedDashboardConfiguration.displayLocalHistogramView
                  "
                  class="my-0 py-0 mr-4"
                  label="Local Histogram"
                  :disabled="
                    editedDashboardConfiguration.currentCategory ==
                    'pointNumberOfAccesses'
                  "
                ></v-checkbox>
                <v-checkbox
                  v-model="
                    editedDashboardConfiguration.displayGlobalHistogramView
                  "
                  class="my-0 py-0 mr-4"
                  label="Global Histogram"
                  :disabled="
                    editedDashboardConfiguration.currentCategory ==
                    'pointNumberOfAccesses'
                  "
                ></v-checkbox>
                <v-checkbox
                  v-model="editedDashboardConfiguration.displayHistogramView"
                  class="my-0 py-0 mr-4"
                  label="Statistics Histogram"
                ></v-checkbox>
              </v-row>
            </exo-card-form>

            <exo-card-form
              label="Histogram settings"
              v-if="
                editedDashboardConfiguration.displayLocalHistogramView ===
                  true ||
                editedDashboardConfiguration.displayGlobalHistogramView ===
                  true ||
                editedDashboardConfiguration.displayHistogramView === true
              "
            >
              <template v-slot:titleTooltip>
                <exo-help-dialog title="Histograms settings" :margin="'m-0'">
                  <tooltipHistogramsSettings />
                </exo-help-dialog>
              </template>
              <v-row no-gutters>
                <v-checkbox
                  v-model="
                    editedDashboardConfiguration.displayOccurenceHistogram
                  "
                  class="my-0 py-0 mr-4"
                  label="Display ocurrence histogram"
                ></v-checkbox>
                <v-checkbox
                  v-model="
                    editedDashboardConfiguration.displayCumulativeHistogram
                  "
                  class="my-0 py-0 mr-4"
                  label="Display cumulative histogram"
                ></v-checkbox>
              </v-row>
              <exo-form-alert
                type="warning"
                v-if="this.isAnyHistogramSelected() === false"
              >
                Warning: no histogram was selected.
              </exo-form-alert>
            </exo-card-form>
          </v-card-text>
          <v-divider class="m-0"></v-divider>
          <v-card-actions class="mx-4 my-3">
            <v-btn
              @click="updateMap()"
              id="updateMapBtn"
              color="primary"
              gt_PerformanceAnalysis-v-step="61"
              >Update map</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
    <v-tour
      name="performanceAnalysisTourDashboard"
      :steps="steps_performance_analysis_dashboard"
      :callbacks="customCallbacks"
      :options="noSkipStepOption"
    ></v-tour>
  </div>
</template>

<script>
import resultsDescription from '../../tools/resultsDescription.json'
import dashboardComponents from '../../components/Dashboard/index.js'
import dataTools from '../../tools/data.js'
import dashboardTools from '../../tools/dashboard.js'
import { mapGetters, mapActions } from 'vuex'
import Vue from 'vue'
import errorMixin from '../../mixins/logHandler.js'
import constellationUtils from '../../mixins/constellationUtils'
import Plotly from 'plotly.js-dist'
import * as Cesium from 'cesium'
import * as d3 from 'd3'
import 'cesium/Widgets/widgets.css'
import tooltipRevisitGroundSegments from '../../components/Tooltips/ConstellationModule/tooltipRevisitGroundSegments'
import tooltipCollectTime from '../../components/Tooltips/ConstellationModule/tooltipCollectTime'
import tooltipHistograms from '../../components/Tooltips/ConstellationModule/tooltipHistograms'
import tooltipHistogramsSettings from '../../components/Tooltips/ConstellationModule/tooltipHistogramsSettings'
import guidedTutorialPerformanceAnalysis, {
  steps_performance_analysis_dashboard
} from '../../components/GuidedTutorials/guidedTutorialPerformanceAnalysis.js'
import { noSkipStepOption } from '../../components/GuidedTutorials/guidedTutorialConstraints.js'
import DashboardViewer from './DashboardViewer.vue'

Vue.use(dashboardComponents)

export default {
  name: 'ConstellationDashboard',
  mixins: [errorMixin, constellationUtils],

  components: {
    tooltipRevisitGroundSegments,
    tooltipCollectTime,
    tooltipHistograms,
    tooltipHistogramsSettings,
    'dashboard-viewer': DashboardViewer
  },

  watch: {
    updateIndex(val) {
      if (this.dataLoaded) {
        Vue.nextTick(() => {
          this.prepareAndDisplayData()
        })
      }
    },
    minZInConfiguration() {
      Vue.set(this.editedDashboardConfiguration, 'zBounds', [
        this.minZInConfiguration,
        this.maxZInConfiguration
      ])
      // not refreshing correctly this.editedDashboardConfiguration.zBounds[0] = this.minZInConfiguration
    },
    maxZInConfiguration() {
      Vue.set(this.editedDashboardConfiguration, 'zBounds', [
        this.minZInConfiguration,
        this.maxZInConfiguration
      ])
      // not refreshing correctly this.editedDashboardConfiguration.zBounds[1] = this.maxZInConfiguration
    },

    displayResult() {
      if (this.gt_PerformanceAnalysis.isActive === true) {
        //setTimeout needed, otherwise the pop-up doesn't show in the right place
        setTimeout(() => {
          this.$tours['performanceAnalysisTourDashboard'].start(1)
        }, 50)
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentMission: 'currentMission',
      currentResultsLog: 'currentResultsLog',
      gt_PerformanceAnalysis: 'gt_PerformanceAnalysis'
    }),

    getPlotBackgroundColor() {
      return this.$vuetify.theme.dark ? '#1e1e1e' : 'white'
    },
    getPlotTextColor() {
      return this.$vuetify.theme.dark ? 'white' : '#1e1e1e'
    },

    displayComputationWidget() {
      return dashboardTools.displayComputationWidget(this)
    },
    displayResult() {
      return dashboardTools.displayResult(this)
    },
    displayError() {
      return dashboardTools.displayError(this)
    },
    displayWarning() {
      return (
        this.emptyResult === true || this.displaySpatialAverageWarning === true
      )
    },
    displaySpatialAverageWarning() {
      if (this.dataLoaded) {
        let isConstantStep = false
        for (const mesh of this.currentResultsLog.results.earthMeshes)
          isConstantStep = isConstantStep || this.isConstantLatitudeStep(mesh)

        const isSpatialAverage =
          this.appliedDashboardConfiguration.displayHistogramView ||
          this.appliedDashboardConfiguration.displayGlobalHistogramView

        return isConstantStep === true && isSpatialAverage === true
      } else {
        return false
      }
    },

    dataLoaded() {
      return dashboardTools.dataLoaded(this)
    },
    dataReady() {
      return this.dataLoaded && this.currentData != null && !this.emptyResult
    },

    loading() {
      return this.currentData == null && this.dataLoaded
    },

    resultLoaded() {
      return (
        this.currentMission?.typeName ===
          'ConstellationPerformanceAnalysisRequest' &&
        this.currentResultsLog?.results != null &&
        this.currentResultsLog.errors != null
      )
    },

    groundStationEphemeridesReady() {
      return (
        this.dataLoaded &&
        this.currentResultsLog.results.groundStationsEphemerides != null &&
        this.currentResultsLog.results.groundStationsEphemerides.length > 0
      )
    },

    revisitEphemeridesReady() {
      return this.dataLoaded && this.currentMission.computeRevisitEphemerides
    },

    mapCategories() {
      if (!this.currentResultsLog || !this.currentResultsLog.results) {
        return []
      }
      let sortedCategories = JSON.parse(
        JSON.stringify(this.currentResultsLog.results.mapCategories)
      )
      sortedCategories.sort()
      return sortedCategories
    },

    availableMapTypes() {
      return this.listAvailableMapTypes(
        this.editedDashboardConfiguration.currentCategory
      )
    },

    availableMeshOptions() {
      const nMeshes = this.currentResultsLog?.results?.earthMeshes?.length
      return [...Array(nMeshes).keys()]
    },

    zFilteredNotNullPerMesh() {
      const zFilteredNotNull = []
      this.currentData.forEach((mesh, meshIndex) => {
        const meshData = []
        mesh.x.forEach((lon, lonIndex) => {
          mesh.y.forEach((lat, latIndex) => {
            const val = mesh.z[latIndex][lonIndex]
            if (val != null) {
              meshData.push(val)
            }
          })
        })
        zFilteredNotNull.push(meshData)
      })
      return zFilteredNotNull
    },

    zFilteredNotNullAllMeshes() {
      const zFilteredNotNull = []
      this.currentData.forEach((mesh, meshIndex) => {
        mesh.x.forEach((lon, lonIndex) => {
          mesh.y.forEach((lat, latIndex) => {
            const val = mesh.z[latIndex][lonIndex]
            if (val != null) {
              zFilteredNotNull.push(val)
            }
          })
        })
      })
      return zFilteredNotNull
    },

    editedZDataUnfiltered() {
      return this.buildZDataUnfiltered(
        this.editedDashboardConfiguration.currentMap
      )
    },

    editedZDataUnfilteredNotNull() {
      let zNotNull = []

      this.editedZDataUnfiltered.forEach((mesh) =>
        mesh.forEach((row) =>
          row.forEach((val) => {
            if (val != null) {
              zNotNull.push(val)
            }
          })
        )
      )
      return zNotNull
    },

    minZInConfiguration() {
      let val = Math.floor(Math.min(...this.editedZDataUnfilteredNotNull))
      if (val.toString().includes('Infinity')) {
        val = this.defaultDashboardConfiguration.zBounds[0]
      }
      return val
    },

    maxZInConfiguration() {
      let val = Math.ceil(Math.max(...this.editedZDataUnfilteredNotNull))
      if (val.toString().includes('Infinity')) {
        val = this.defaultDashboardConfiguration.zBounds[1]
      }
      return val
    },

    stepData() {
      return Math.max(
        1,
        Math.ceil((this.maxZInConfiguration - this.minZInConfiguration) / 100)
      )
    },

    groundStationNames() {
      if (this.currentMission?.groundStations != null) {
        return this.currentMission.groundStations.map(
          (groundStation) => groundStation.name
        )
      }
      return []
    },

    startOfSimulationDate() {
      if (this.currentMission?.simulationDate) {
        return new Date(this.currentMission.simulationDate)
      } else {
        return new Date()
      }
    },

    endOfSimulationDate() {
      if (this.currentMission?.simulationDuration) {
        return this.computeDateFromDuration(
          this.currentMission.simulationDuration
        )
      } else {
        return new Date()
      }
    },

    groundStationTimelineAvailable() {
      return this.currentResultsLog?.results?.stationVisibilityTimeline != null
    },

    inputBinSizeFeedback() {
      let feedback = true
      let binSize = Number(this.editedDashboardConfiguration.histogramBinSize)

      if (binSize !== null) {
        if (isNaN(binSize) || binSize <= 0) {
          feedback = 'Please input a valid positive number.'
        }
      }
      return feedback
    },

    inputBinNumberFeedback() {
      let feedback = true
      let binNumber = Number(
        this.editedDashboardConfiguration.histogramNumberOfBins
      )

      if (binNumber !== null) {
        if (isNaN(binNumber) || binNumber <= 1) {
          feedback = 'Please input a valid number >1.'
        }
      }
      return feedback
    },

    validHistogramInputs() {
      if (this.lastHistogramOptionUpdated == 'Number of bins') {
        return this.inputBinNumberFeedback === true
      } else if (this.lastHistogramOptionUpdated == 'Bin size') {
        return this.inputBinSizeFeedback === true
      } else {
        return (
          this.inputBinSizeFeedback === true ||
          this.inputBinNumberFeedback === true
        )
      }
    },

    inputLatitudeFeedback() {
      let feedback = true
      let latitude = Number(this.singlePointLatitude)

      if (latitude !== null) {
        if (isNaN(latitude) || latitude < -90 || latitude > 90) {
          feedback =
            'Please input a valid number between -90 and 90 (inclusive).'
        }
      }
      return feedback
    },

    inputLongitudeFeedback() {
      let feedback = true
      let longitude = Number(this.singlePointLongitude)

      if (longitude !== null) {
        if (isNaN(longitude) || longitude < -180 || longitude > 180) {
          feedback =
            'Please input a valid number between -180 and 180 (inclusive).'
        }
      }
      return feedback
    },

    validCoordinatesInputs() {
      return (
        this.inputLatitudeFeedback === true &&
        this.inputLongitudeFeedback === true
      )
    },

    appliedMetricName() {
      if (
        this.appliedDashboardConfiguration.currentCategory ==
        'pointNumberOfAccesses'
      ) {
        return this.getResultsFieldLabel(
          this.appliedDashboardConfiguration.currentCategory
        )
      } else {
        return (
          this.getResultsFieldLabel(
            this.appliedDashboardConfiguration.currentMap
          ) +
          ' ' +
          this.getResultsFieldLabel(
            this.appliedDashboardConfiguration.currentCategory
          ).toLowerCase()
        )
      }
    }
  },

  data() {
    return {
      editedDashboardConfiguration: {
        zBounds: [-1, 1],
        displayTimelines: true,
        display3DView: true,
        displayHeatmapView: true,
        displayHistogramView: true,
        displayLocalHistogramView: true,
        displayGlobalHistogramView: true,
        histogramBinSize: '',
        histogramNumberOfBins: '',
        displayOccurenceHistogram: true,
        displayCumulativeHistogram: true,
        interpolate: false,
        currentCategory: 'pointRevisitTime',
        currentMap: 'pointRevisitTimeAverage',
        currentColorMap: 'Jet',
        currentPercentileIndex: 0
        // TODO flat/globe + position
      },
      appliedDashboardConfiguration: {
        zBounds: [-1, 1],
        displayTimelines: true,
        display3DView: true,
        displayHeatmapView: true,
        displayHistogramView: true,
        displayLocalHistogramView: true,
        displayGlobalHistogramView: true,
        histogramBinSize: '',
        histogramNumberOfBins: '',
        displayOccurenceHistogram: true,
        displayCumulativeHistogram: true,
        interpolate: false,
        currentCategory: 'pointRevisitTime',
        currentMap: 'pointRevisitTimeAverage',
        currentColorMap: 'Jet',
        currentPercentileIndex: 0
        // TODO flat/globe + position
      },
      defaultDashboardConfiguration: {
        zBounds: [-1, 1],
        displayTimelines: true,
        display3DView: true,
        displayHeatmapView: true,
        displayHistogramView: true,
        displayLocalHistogramView: true,
        displayGlobalHistogramView: true,
        histogramBinSize: '',
        histogramNumberOfBins: '',
        displayOccurenceHistogram: true,
        displayCumulativeHistogram: true,
        interpolate: false,
        currentCategory: 'pointRevisitTime',
        currentMap: 'pointRevisitTimeAverage',
        currentColorMap: 'Jet',
        currentPercentileIndex: 0
        // TODO flat/globe + position
      },

      ID_SEPARATOR: '#/#',
      currentRevisitTimelineLabel: null,
      revisitTimelineCategory: '',
      availableRevisitTimelines: [],
      revisitTimelineIntervals: {},
      currentGroundStationTimelineLabel: null,
      groundStationTimelineCategory: '',
      availableGroundStationTimelines: [],
      groundStationTimelineIntervals: {},
      currentGroundStationName: '',
      idWidgetHeatmapView: 'plotingAreaHeat',
      idWidgetHistogramView: 'plotingMetricHistogram',
      idWidgetLocalHistogramView: 'plotingLocalHistogram',
      idWidgetGlobalHistogramView: 'plotingGlobalHistogram',
      statisticsHistogramData: [],
      globalHistogramData: [],
      localHistogramData: [],
      localHistogramDataToPlot: [],
      buildingMap: false,
      heatMapHasUndefinedLocation: false,
      constellationsNameByIndex: {},
      dialog: false,
      exportDialog: false,
      settingsDialog: false,
      loadingSinglePointResults: false,
      singlePointLatitude: 0,
      singlePointLatStr: 0,
      singlePointLongitude: 0,
      singlePointLonStr: 0,
      singlePointResponse: null,
      terrainProvider: null,
      viewer: null,
      data: [],
      rectangles: [],
      factorX: 16,
      factorY: 16,
      mapboxToken:
        'pk.eyJ1IjoiZXhvdHJhaWwiLCJhIjoiY2szcTNnZmFkMDkzbzNtcDhzMm0ydmJ5YyJ9.5so1MY_ou6yoPmLAzLchUg',
      bounds: null,
      label: null,
      mapTypes: [
        'pointSimultaneousSpacecraftsAverage',
        'pointSimultaneousSpacecraftsMedian',
        'pointSimultaneousSpacecraftsMax',
        'pointSimultaneousSpacecraftsMin',
        'pointSimultaneousSpacecraftsPercentile',
        'pointCollectTimeAverage',
        'pointCollectTimeMedian',
        'pointCollectTimeMax',
        'pointCollectTimeMin',
        'pointCollectTimePercentile',
        'pointAccessDurationAverage',
        'pointAccessDurationMedian',
        'pointAccessDurationMax',
        'pointAccessDurationMin',
        'pointAccessDurationPercentile',
        'pointAccessDurationCumulative',
        'pointRevisitTimeAverage',
        'pointRevisitTimeMedian',
        'pointRevisitTimeMax',
        'pointRevisitTimeMin',
        'pointRevisitTimePercentile',
        'forwardToReverseLinkTimeAverage',
        'forwardToReverseLinkTimeMedian',
        'forwardToReverseLinkTimeMax',
        'forwardToReverseLinkTimeMin',
        'forwardToReverseLinkTimePercentile',
        'reverseLinkTransferTimeAverage',
        'reverseLinkTransferTimeMedian',
        'reverseLinkTransferTimeMax',
        'reverseLinkTransferTimeMin',
        'reverseLinkTransferTimePercentile',
        'forwardLinkTransferTimeAverage',
        'forwardLinkTransferTimeMedian',
        'forwardLinkTransferTimeMax',
        'forwardLinkTransferTimeMin',
        'forwardLinkTransferTimePercentile',
        'timeBetweenReverseLinkArrivalsAverage',
        'timeBetweenReverseLinkArrivalsMedian',
        'timeBetweenReverseLinkArrivalsMax',
        'timeBetweenReverseLinkArrivalsMin',
        'timeBetweenReverseLinkArrivalsPercentile',
        'pointNumberOfAccesses'
      ],
      unitTypes: {
        pointAccessDurationAverage: 's',
        pointAccessDurationMedian: 's',
        pointAccessDurationMax: 's',
        pointAccessDurationMin: 's',
        pointAccessDurationPercentile: 's',
        pointAccessDurationCumulative: 's',
        pointRevisitTimeAverage: 's',
        pointRevisitTimeMedian: 's',
        pointRevisitTimeMax: 's',
        pointRevisitTimeMin: 's',
        pointRevisitTimePercentile: 's',
        pointSimultaneousSpacecraftsAverage: '',
        pointSimultaneousSpacecraftsMedian: '',
        pointSimultaneousSpacecraftsMax: '',
        pointSimultaneousSpacecraftsMin: '',
        pointSimultaneousSpacecraftsPercentile: '',
        pointCollectTimeAverage: 's',
        pointCollectTimeMedian: 's',
        pointCollectTimeMax: 's',
        pointCollectTimeMin: 's',
        pointCollectTimePercentile: 's',
        forwardToReverseLinkTimeAverage: 's',
        forwardToReverseLinkTimeMedian: 's',
        forwardToReverseLinkTimeMax: 's',
        forwardToReverseLinkTimeMin: 's',
        forwardToReverseLinkTimePercentile: 's',
        reverseLinkTransferTimeAverage: 's',
        reverseLinkTransferTimeMedian: 's',
        reverseLinkTransferTimeMax: 's',
        reverseLinkTransferTimeMin: 's',
        reverseLinkTransferTimePercentile: 's',
        forwardLinkTransferTimeAverage: 's',
        forwardLinkTransferTimeMedian: 's',
        forwardLinkTransferTimeMax: 's',
        forwardLinkTransferTimeMin: 's',
        forwardLinkTransferTimePercentile: 's',
        timeBetweenReverseLinkArrivalsAverage: 's',
        timeBetweenReverseLinkArrivalsMedian: 's',
        timeBetweenReverseLinkArrivalsMax: 's',
        timeBetweenReverseLinkArrivalsMin: 's',
        timeBetweenReverseLinkArrivalsPercentile: 's',
        pointNumberOfAccesses: ''
      },
      updateIndex: 0,
      colorsMaps: ['Viridis', 'Magma', 'YlGnBu', 'Jet'],
      heatMapCommons: {
        colorScale: [],
        currentGradient: [],
        gradientStep: 0,
        tickText: [],
        tickValues: [],
        zMax: 10e3,
        zMin: 0
      },
      displayHistogramOptions: true,
      lastHistogramOptionUpdated: '',
      currentData: null,
      emptyResult: false,
      ellipsoid: null,
      type: 'ConstellationPerformanceAnalysisResult',
      steps_performance_analysis_dashboard:
        steps_performance_analysis_dashboard,
      noSkipStepOption: noSkipStepOption,
      isGuidedTutorialInDisplaySettings: false,
      customCallbacks: {
        onNextStep: (currentStep) => {
          guidedTutorialPerformanceAnalysis.onNextStepDashboard(
            currentStep,
            this
          )
        },
        onSkip: () => {
          guidedTutorialPerformanceAnalysis.stopAllTours(this)
        },
        onFinish: () => {
          guidedTutorialPerformanceAnalysis.stopAllTours(this)
        }
      },
      heatmapMeshIndex: 0,
      statisticsHistogramMeshIndex: 0,
      allMeshesIndex: 'All meshes'
    }
  },
  mounted() {
    this.$root.$on('current-results-updated', this.currentResultsUpdatedHandler)
    setTimeout(() => {
      this.loadLayout()
    }, 1)
    let earthRadiusEGM96 = 6378136.3
    this.ellipsoid = new Cesium.Ellipsoid(
      earthRadiusEGM96,
      earthRadiusEGM96,
      earthRadiusEGM96
    )
    if (this.gt_PerformanceAnalysis.isActive === true) {
      this.$tours['performanceAnalysisTourDashboard'].start()
    }
  },

  beforeDestroy() {
    // When switching page, purge plotly context to avoid memory leaks and errors when resizing page
    // No catch if the plot didn't rendered
    this.currentData = null
    this.emptyResult = false
    this.cleanViews()
    this.$root.$off(
      'current-results-updated',
      this.currentResultsUpdatedHandler
    )
  },

  methods: {
    ...mapActions(['start_gt_PerformanceAnalysis']),

    currentResultsUpdatedHandler() {
      setTimeout(() => {
        this.loadLayout()
      }, 1)
    },

    categoryUpdated() {
      this.updateDefaultMapFromCategory(this.editedDashboardConfiguration)
    },

    meshIndexUpdated() {
      this.updateMap()
    },

    updateDefaultMapFromCategory(configuration) {
      if (this.currentResultsLog?.results?.mapCategories == null) {
        configuration.currentCategory = null
        configuration.currentMap = null
        return
      }
      if (
        configuration.currentCategory == null ||
        !this.currentResultsLog.results.mapCategories.includes(
          configuration.currentCategory
        )
      ) {
        configuration.currentCategory =
          this.currentResultsLog.results.mapCategories[0]
      }
      let currentMapTypes = this.listAvailableMapTypes(
        configuration.currentCategory
      )
      if (
        configuration.currentMap == null ||
        !currentMapTypes.includes(configuration.currentMap)
      ) {
        configuration.currentMap = currentMapTypes[0]
      }
    },

    listAvailableMapTypes(currentCategory) {
      return this.mapTypes.filter((el) => el.startsWith(currentCategory))
    },

    openDashboardSettings() {
      this.editedDashboardConfiguration = JSON.parse(
        JSON.stringify(this.appliedDashboardConfiguration)
      )
      if (
        this.editedDashboardConfiguration.zBounds[0] ===
          this.defaultDashboardConfiguration.zBounds[0] &&
        this.editedDashboardConfiguration.zBounds[1] ===
          this.defaultDashboardConfiguration.zBounds[1]
      ) {
        Vue.set(this.editedDashboardConfiguration, 'zBounds', [
          this.minZInConfiguration,
          this.maxZInConfiguration
        ])
      }

      this.settingsDialog = true
      if (this.gt_PerformanceAnalysis.isActive === true) {
        this.$tours['performanceAnalysisTourDashboard'].start(5)
      }
    },

    buildZDataUnfiltered(currentMap) {
      if (
        !this.currentResultsLog ||
        !this.currentResultsLog.results ||
        !this.currentResultsLog.results.meshPerformances
      ) {
        return []
      }
      const data = []

      let iLon = 0
      let row = []
      const self = this
      this.currentResultsLog.results.meshPerformances.forEach(function (
        mesh,
        index
      ) {
        const meshData = []
        const nLongitudes =
          self.currentResultsLog.results.earthMeshes[index].longitudes.length
        for (const point of mesh.points) {
          const value = self.getAppliedMetricValue(point, currentMap)
          row.push(value)
          iLon += 1
          if (iLon >= nLongitudes) {
            meshData.push(row)
            row = []
            iLon = 0
          }
        }

        data.push(meshData)
      })

      return data
    },

    getAppliedMetricValue(point, currentMap) {
      if (currentMap == 'pointNumberOfAccesses') {
        return point.metrics.numberOfAccesses
      }

      const metricName = this.getStatisticalMetricName(currentMap)
      const metricStatistic = this.getStatisticalMetricStatistic(currentMap)
      if (
        !(metricName in point.metrics.statisticalMetrics) ||
        !(metricStatistic in point.metrics.statisticalMetrics[metricName])
      ) {
        return null
      }

      let value = point.metrics.statisticalMetrics[metricName][metricStatistic]

      if (value != null && metricStatistic === 'percentiles') {
        value = value[this.appliedDashboardConfiguration.currentPercentileIndex]
      }

      value = value === 'NaN' || value === 'Infinity' ? null : value
      return value
    },

    computeDateFromDuration(secondsSinceStart) {
      if (this.currentMission?.simulationDate) {
        let newDate = new Date(this.currentMission.simulationDate)
        newDate.setSeconds(newDate.getSeconds() + secondsSinceStart)
        return newDate
      } else {
        return new Date()
      }
    },

    isAnyHistogramSelected() {
      return (
        this.editedDashboardConfiguration.displayOccurenceHistogram === true ||
        this.editedDashboardConfiguration.displayCumulativeHistogram === true
      )
    },

    exportGroundStationEphemerides() {
      let dataToExport = ''
      this.currentResultsLog.results.groundStationsEphemerides.forEach(
        (element) => {
          dataToExport += element + '\n'
        }
      )

      dataTools.downloadFile(
        this.currentMission.name + '_gs_visibilities',
        dataToExport
      )
    },

    exportRevisitEphemerides() {
      this.$http
        .get(
          this.getCurrentAPI() + '/results_revisit/' + this.currentMission.id
        )
        .then((response) => {
          let dataToExport = ''
          response.data.forEach((element) => {
            dataToExport += element + '\n'
          })
          dataTools.downloadFile(
            this.currentMission.name + '_revisit_ephemerides',
            dataToExport
          )
        })
    },

    computeStatisticsHistogramData(data) {
      if (data == null || data.length == 0) {
        return []
      }
      this.statisticsHistogramData = []

      let totalNumberOfPoints = data[0].x.length
      let xbinsStart = data[0].xbins.start - data[0].xbins.size / 2
      let xbinsEnd = data[0].xbins.end + data[0].xbins.size / 2
      if (xbinsStart == xbinsEnd) {
        xbinsStart = this.minZInConfiguration - 0.5
        xbinsEnd = this.maxZInConfiguration + 0.5
      }

      let numberOfBins = Math.sqrt(totalNumberOfPoints) //first estimate
      let xbinsSize = data[0].xbins.size
      if (xbinsSize == '') {
        xbinsSize = Math.ceil((xbinsEnd - xbinsStart) / numberOfBins)
      }
      numberOfBins =
        1 + Math.ceil((data[0].xbins.end - data[0].xbins.start) / xbinsSize)

      let binMiddles = []
      let binEnds = []
      for (let iBin = 0; iBin < numberOfBins; iBin++) {
        binMiddles.push(xbinsStart + xbinsSize / 2 + xbinsSize * iBin)
        binEnds.push(xbinsStart + (iBin + 1) * xbinsSize)
      }

      let yOccurence = new Array(numberOfBins).fill(0)
      let yCumulative = new Array(numberOfBins).fill(0)

      let histogram = data[0]
      //the sorting function is necessary because otherwise the array entries will be treated as string (100<2)
      let xData = histogram.x.slice().sort(function (a, b) {
        return a - b
      })
      let currentiBin = 0
      let cumulatedValue = 0
      xData.forEach((value) => {
        cumulatedValue += 1 / totalNumberOfPoints
        if (value <= binEnds[currentiBin]) {
          yCumulative[currentiBin] = cumulatedValue
          yOccurence[currentiBin] += 1 / totalNumberOfPoints
        } else {
          while (value > binEnds[currentiBin]) {
            yCumulative[currentiBin] = cumulatedValue
            currentiBin++
          }
          yOccurence[currentiBin] += 1 / totalNumberOfPoints
          yCumulative[currentiBin] = cumulatedValue
        }
      })
      if (
        this.appliedDashboardConfiguration.displayOccurenceHistogram === true
      ) {
        this.statisticsHistogramData.push({ x: binMiddles, y: yOccurence })
      }
      if (
        this.appliedDashboardConfiguration.displayCumulativeHistogram === true
      ) {
        this.statisticsHistogramData.push({ x: binMiddles, y: yCumulative })
      }
    },

    updateStatisticsHistogramData(xbinsSize) {
      if (!xbinsSize) {
        return
      }

      let data = []
      const xData =
        this.statisticsHistogramMeshIndex === this.allMeshesIndex
          ? this.zFilteredNotNullAllMeshes
          : this.zFilteredNotNullPerMesh[this.statisticsHistogramMeshIndex]

      data.push({
        xbins: {
          size: xbinsSize,
          start:
            this.maxZInConfiguration == this.minZInConfiguration
              ? ''
              : this.minZInConfiguration,
          end:
            this.maxZInConfiguration == this.minZInConfiguration
              ? ''
              : this.maxZInConfiguration
        },
        x: xData
      })

      this.computeStatisticsHistogramData(data)
    },

    exportHistogram(histogramData, histogramFileName) {
      let dataToExport = ''
      dataToExport += 'Bin middle'
      dataToExport += this.appliedDashboardConfiguration
        .displayOccurenceHistogram
        ? ', % of total incidences (occurence)'
        : ''
      dataToExport += this.appliedDashboardConfiguration
        .displayCumulativeHistogram
        ? ', % of total incidences (cumulative)'
        : ''
      dataToExport += '\n'

      for (let i = 0; i < histogramData[0].x.length; i++) {
        dataToExport +=
          histogramData[0].x[i].toFixed(2) +
          ',' +
          (100 * histogramData[0].y[i]).toFixed(1)
        dataToExport +=
          histogramData.length == 2
            ? ',' + (100 * histogramData[1].y[i]).toFixed(1)
            : ''
        dataToExport += '\n'
      }

      dataTools.downloadFile(
        this.currentMission.name + histogramFileName,
        dataToExport
      )
    },

    exportStatisticsHistogram() {
      this.exportHistogram(
        this.statisticsHistogramData,
        '_statistics_histogram'
      )
    },

    exportGlobalHistogram() {
      this.exportHistogram(this.globalHistogramData, '_global_histogram')
    },

    exportLocalHistogram() {
      this.exportHistogram(this.localHistogramData, '_local_histogram')
    },

    resetLayout() {
      this.saveLayout(this.defaultDashboardConfiguration)
    },
    saveCurrentLayout() {
      this.saveLayout(this.appliedDashboardConfiguration)
    },

    saveLayout(layout) {
      this.currentMission.layout = JSON.stringify(layout)
      this.$http
        .put(
          this.getCurrentAPI() + '/' + this.currentMission.id + '/minorupdate',
          this.currentMission
        )
        .then((response) => {
          this.$root.$emit('set-current-mission', response.data)
        })
    },

    cleanHeatmapView() {
      try {
        Plotly.purge(this.$refs[this.idWidgetHeatmapView])
      } catch (e) {}
    },

    cleanHistogramView() {
      this.statisticsHistogramData = []
      try {
        Plotly.purge(this.$refs[this.idWidgetHistogramView])
      } catch (e) {}
    },

    cleanLocalHistogramView() {
      this.localHistogramData = []
      try {
        Plotly.purge(this.$refs[this.idWidgetLocalHistogramView])
      } catch (e) {}
    },

    cleanGlobalHistogramView() {
      this.globalHistogramData = []
      try {
        Plotly.purge(this.$refs[this.idWidgetGlobalHistogramView])
      } catch (e) {}
    },

    loadLayout() {
      if (this.dataLoaded && !this.buildingMap) {
        this.editedDashboardConfiguration = JSON.parse(
          JSON.stringify(this.defaultDashboardConfiguration)
        )

        if (this.currentMission.layout) {
          const savedConfiguration = JSON.parse(this.currentMission.layout)
          for (let field of Object.keys(this.editedDashboardConfiguration)) {
            if (savedConfiguration[field] != null) {
              this.editedDashboardConfiguration[field] =
                savedConfiguration[field]
            }
          }
        }
        Vue.nextTick(() => {
          this.updateMap()
        })
      }
    },

    async computeGraphData(iLat, iLon) {
      const singleSimulation = {
        simulation: this.currentMission, // earth radius
        iLat: iLat,
        iLon: iLon
      }
      let data = await this.$http
        .post('/physics/computeraan', singleSimulation)
        .then(function (response) {
          return response.data
        })
        .catch((e) => {})
      return data
    },

    valueToUnitText(value) {
      if (
        this.unitTypes[this.appliedDashboardConfiguration.currentMap] === 's'
      ) {
        return value != null && !isNaN(value)
          ? dataTools.sec2DDHHMMSS(value)
          : 'undefined'
      } else if (
        this.appliedDashboardConfiguration.currentCategory ===
        'pointSimultaneousSpacecrafts'
      ) {
        if (value != null) {
          return _.isString(value) ? value : value.toFixed(1)
        } else {
          return 'undefined'
        }
      } else {
        if (value != null) {
          return _.isString(value) ? value : value.toFixed()
        } else {
          return 'undefined'
        }
      }
    },

    valueToUnitTextFromType(value, type) {
      if (this.unitTypes[type] === 's') {
        return value != null && !isNaN(value)
          ? dataTools.sec2DDHHMMSS(value)
          : 'undefined'
      } else if (type.includes('pointSimultaneousSpacecrafts')) {
        if (value != null) {
          return _.isString(value) ? value : value.toFixed(1)
        } else {
          return 'undefined'
        }
      } else {
        if (value != null) {
          return _.isString(value) ? value : value.toFixed()
        } else {
          return 'undefined'
        }
      }
    },

    requestComputation() {
      this.$router.push({ path: '/analyticalconstellationsimulation' })
      Vue.nextTick(() => {
        // use nextTick to be sure mission components are created and can handle events
        this.$root.$emit('request-computation-constellation-analysis')
      })
    },

    updateMap() {
      if (
        this.settingsDialog === true &&
        this.gt_PerformanceAnalysis.isActive === true
      ) {
        this.$tours['performanceAnalysisTourDashboard'].start(9)
      }
      this.cleanViews()
      this.settingsDialog = false
      this.appliedDashboardConfiguration = JSON.parse(
        JSON.stringify(this.editedDashboardConfiguration)
      )

      // correct zbounds before updating the map
      if (
        this.appliedDashboardConfiguration.zBounds != null &&
        this.appliedDashboardConfiguration.zBounds[0] !== -1
      ) {
        if (
          this.appliedDashboardConfiguration.zBounds[0] <
            this.minZInConfiguration ||
          this.appliedDashboardConfiguration.zBounds[1] >
            this.maxZInConfiguration
        ) {
          this.appliedDashboardConfiguration.zBounds[0] =
            this.minZInConfiguration
          this.appliedDashboardConfiguration.zBounds[1] =
            this.maxZInConfiguration
        }
      }

      if (this.appliedDashboardConfiguration.interpolate) {
        this.factorX = 1
        this.factorY = 1
      } else {
        this.factorX = 16
        this.factorY = 16
      }
      if (this.isAnyHistogramSelected() === false) {
        this.appliedDashboardConfiguration.displayHistogramView = false
        this.appliedDashboardConfiguration.displayGlobalHistogramView = false
        this.appliedDashboardConfiguration.displayLocalHistogramView = false
      } else if (
        this.appliedDashboardConfiguration.currentCategory ==
        'pointNumberOfAccesses'
      ) {
        this.appliedDashboardConfiguration.displayGlobalHistogramView = false
        this.appliedDashboardConfiguration.displayLocalHistogramView = false
      }

      this.updateIndex++
    },

    displaySinglePointResultsFromLatLon() {
      let lat = dataTools.deg2rad(this.singlePointLatitude)
      let lon = dataTools.deg2rad(this.singlePointLongitude)

      this.displaySinglePointResults(lat, lon)
    },

    updateHistogram() {
      this.appliedDashboardConfiguration = JSON.parse(
        JSON.stringify(this.editedDashboardConfiguration)
      )

      // correct zbounds before upadteing the histogram
      if (
        this.appliedDashboardConfiguration.zBounds != null &&
        this.appliedDashboardConfiguration.zBounds[0] !== -1
      ) {
        if (
          this.appliedDashboardConfiguration.zBounds[0] <
            this.minZInConfiguration ||
          this.appliedDashboardConfiguration.zBounds[1] >
            this.maxZInConfiguration
        ) {
          this.appliedDashboardConfiguration.zBounds[0] =
            this.minZInConfiguration
          this.appliedDashboardConfiguration.zBounds[1] =
            this.maxZInConfiguration
        }
      }

      let xbinsSize
      let dataRange = this.maxZInConfiguration - this.minZInConfiguration
      if (this.lastHistogramOptionUpdated == 'Bin size') {
        xbinsSize = this.appliedDashboardConfiguration.histogramBinSize
        this.editedDashboardConfiguration.histogramNumberOfBins = Math.ceil(
          dataRange / xbinsSize
        )
        this.appliedDashboardConfiguration.histogramNumberOfBins = Math.ceil(
          dataRange / xbinsSize
        )
      } else {
        xbinsSize =
          dataRange /
          Math.ceil(
            this.appliedDashboardConfiguration.histogramNumberOfBins - 1
          )
        if (xbinsSize < 0.1) {
          xbinsSize = 0.1
          this.editedDashboardConfiguration.histogramNumberOfBins = Math.ceil(
            dataRange / xbinsSize
          )
          this.appliedDashboardConfiguration.histogramNumberOfBins = Math.ceil(
            dataRange / xbinsSize
          )
        }
        this.editedDashboardConfiguration.histogramBinSize =
          Math.round(10 * xbinsSize) / 10
        this.appliedDashboardConfiguration.histogramBinSize =
          Math.round(10 * xbinsSize) / 10
      }
      Plotly.restyle(this.idWidgetHistogramView, { 'xbins.size': xbinsSize })
      this.updateStatisticsHistogramData(xbinsSize)
    },

    generateData() {
      this.currentData = []
      if (!this.currentResultsLog.results.meshPerformances) {
        return
      }
      let zData = this.buildZDataUnfiltered(
        this.appliedDashboardConfiguration.currentMap
      )
      if (this.appliedDashboardConfiguration?.zBounds != null) {
        if (this.appliedDashboardConfiguration.zBounds[0] !== -1) {
          const min = this.appliedDashboardConfiguration.zBounds[0]
          const max = this.appliedDashboardConfiguration.zBounds[1]
          zData = zData.map((mesh) =>
            mesh.map((row) =>
              row.map((el) => {
                return el > max || el < min ? null : el
              })
            )
          )
        }
      }
      const self = this
      this.currentResultsLog.results.earthMeshes.forEach(function (
        mesh,
        index
      ) {
        self.currentData.push({
          x: mesh.longitudes.map((el) =>
            dataTools.convertUnits(el, 'rad', 'deg')
          ),
          y: mesh.latitudes.map((el) =>
            dataTools.convertUnits(el, 'rad', 'deg')
          ),
          z: zData[index]
        })
      })
    },

    generateHistogramDataFromResults(results) {
      let histogramData = {}
      let iBin = 0
      let binStart = []
      let binEnd = []
      let binMiddle = []
      let binValue = []
      let binValueCumulative = []
      let applyFilter = false
      let isWithinFilter = true

      if (isNaN(results[0][0])) {
        return null
      }

      if (this.appliedDashboardConfiguration?.zBounds != null) {
        if (this.appliedDashboardConfiguration.zBounds[0] !== -1) {
          applyFilter = true
        }
      }

      applyFilter = false //TODO change a volonte
      for (iBin = 0; iBin < results.length; iBin++) {
        isWithinFilter =
          results[iBin][0] > this.appliedDashboardConfiguration.zBounds[0] &&
          results[iBin][1] < this.appliedDashboardConfiguration.zBounds[1]
        if (applyFilter === false || isWithinFilter === true) {
          binStart.push(results[iBin][0])
          binEnd.push(results[iBin][1])
          binMiddle.push(
            0.5 * (binStart[binStart.length - 1] + binEnd[binEnd.length - 1])
          )
          binValue.push(results[iBin][2])
          if (binValueCumulative.length > 0) {
            binValueCumulative.push(
              binValueCumulative[binValueCumulative.length - 1] +
                binValue[binValue.length - 1]
            )
          } else {
            binValueCumulative.push(binValue[binValue.length - 1])
          }
        }
      }

      histogramData['binStart'] = binStart
      histogramData['binEnd'] = binEnd
      histogramData['binMiddle'] = binMiddle
      histogramData['binValue'] = binValue
      histogramData['binValueCumulative'] = binValueCumulative

      return histogramData
    },

    generateGradient() {
      this.heatMapCommons.currentGradient = constellationUtils.getGradient(
        this.appliedDashboardConfiguration.currentColorMap
      )
      this.heatMapCommons.colorscale = this.generatePlotlyColorScale()
      let zNotNull = this.zFilteredNotNullAllMeshes

      this.emptyResult = zNotNull.length === 0
      this.heatMapCommons.zMin = Math.min(...zNotNull)
      this.heatMapCommons.zMax = Math.max(...zNotNull)
      this.generatePlotlyHeatmapTicks()
    },
    convertToHourFormat(seconds) {
      let hours = Math.floor(seconds / 3600)
      let minutes = Math.floor((seconds - hours * 3600) / 60)
      let second = Math.floor(seconds - hours * 3600 - minutes * 60)

      return String(hours + 'h ' + minutes + 'min ' + second) + 's '
    },

    displaySinglePointResults(lat, lon) {
      this.cleanRevisitTimelines()
      this.cleanLocalHistogramView()

      if (
        !this.singlePointResponse ||
        this.singlePointLatitude != dataTools.rad2deg(lat).toFixed(2) ||
        this.singlePointLongitude != dataTools.rad2deg(lon).toFixed(2)
      ) {
        this.loadingSinglePointResults = true
        this.computeSinglePointResults(lat, lon)
      } else {
        this.displayComputedSinglePointResults()
      }
    },

    computeSinglePointResults(lat, lon) {
      this.singlePointLatitude = dataTools.rad2deg(lat).toFixed(2)
      this.singlePointLongitude = dataTools.rad2deg(lon).toFixed(2)
      this.singlePointLonStr = dataTools.formatAngle(dataTools.rad2deg(lon))
      this.singlePointLatStr = dataTools.formatAngle(dataTools.rad2deg(lat))
      this.revisitTimelineCategory =
        'Revisit on latitude ' +
        this.singlePointLatStr +
        ' / longitude ' +
        this.singlePointLonStr
      let self = this
      this.$http
        .post(
          this.getCurrentAPI() + '/single_point/' + this.currentMission.id,
          { lat: lat, lon: lon }
        )
        .then((response) => {
          self.singlePointResponse = response
          self.displayComputedSinglePointResults()
          self.loadingSinglePointResults = false
        })
    },

    displayComputedSinglePointResults() {
      if (this.appliedDashboardConfiguration.displayTimelines) {
        for (let key of Object.keys(
          this.singlePointResponse.data.revisitTimeline
        )) {
          const isDefault = key.startsWith('All')
          this.addTimeline(
            key,
            this.singlePointResponse.data.revisitTimeline[key],
            isDefault,
            'revisitTimeline'
          )
        }
      }

      this.localHistogramDataToPlot =
        this.singlePointResponse.data.globalHistograms
      if (this.appliedDashboardConfiguration.displayLocalHistogramView) {
        this.drawLocalHistogram()
      }
    },

    displayGroundStationTimelineFrom3DView(objectName) {
      for (let groundStationName of this.groundStationNames) {
        if (objectName.includes(groundStationName)) {
          this.currentGroundStationName = groundStationName
          this.displayGroundStationTimelineFromCache()
        }
      }
    },

    displayGroundStationTimelineFromCache() {
      if (document.getElementById('groundStationTimeline') == null) {
        //if ground station visibility timeline is not yet loaded
        setTimeout(() => {
          this.displayGroundStationTimelineFromCache()
        }, 100)
      } else {
        this.displayGroundStationTimeline(this.currentGroundStationName)
      }
    },

    displayGroundStationTimeline(stationName) {
      if (this.groundStationTimelineAvailable) {
        this.cleanGroundStationTimelines()
        this.groundStationTimelineCategory = 'Ground station visibility'
        const keys = Object.keys(
          this.currentResultsLog.results.stationVisibilityTimeline
        )
        for (let key of keys) {
          const keyElements = key.split(this.ID_SEPARATOR)
          const groundStationName = keyElements[0]
          const constellationName = keyElements[1]
          if (groundStationName === stationName) {
            const isDefault = constellationName.startsWith('All')
            this.addTimeline(
              key,
              this.currentResultsLog.results.stationVisibilityTimeline[key],
              isDefault,
              'groundStationTimeline'
            )
          }
        }
      }
    },

    addTimeline(key, intervals, isDefault, timelineId) {
      const regex = new RegExp(this.ID_SEPARATOR, 'g')
      const label = key.replace(regex, ' - ')
      if (timelineId == 'revisitTimeline') {
        this.availableRevisitTimelines.push(label)
        this.availableRevisitTimelines.sort()
        this.revisitTimelineIntervals[label] = intervals

        if (isDefault || this.currentRevisitTimelineLabel == null) {
          this.currentRevisitTimelineLabel = label
          this.updateRevisitTimeline()
        }
      } else {
        this.availableGroundStationTimelines.push(label)
        this.availableGroundStationTimelines.sort()
        this.groundStationTimelineIntervals[label] = intervals

        if (isDefault || this.currentGroundStationTimelineLabel == null) {
          this.currentGroundStationTimelineLabel = label
          this.updateGroundStationTimeline()
        }
      }
    },

    cleanTimelines() {
      this.cleanRevisitTimelines()
      this.cleanGroundStationTimelines()
    },

    cleanRevisitTimelines() {
      this.currentRevisitTimelineLabel = null
      this.availableRevisitTimelines = []
      this.revisitTimelineIntervals = {}
      this.revisitTimelineCategory = ''
      this.cleanRevisitTimelineView()
    },

    cleanGroundStationTimelines() {
      this.currentGroundStationTimelineLabel = null
      this.availableGroundStationTimelines = []
      this.groundStationTimelineIntervals = {}
      this.groundStationTimelineCategory = ''
      this.cleanGroundStationTimelineView()
    },

    cleanRevisitTimelineView() {
      d3.select('#revisitTimeline')
        .selectAll('*')
        .remove()
        .selectAll('rect')
        .remove()
    },

    cleanGroundStationTimelineView() {
      d3.select('#groundStationTimeline')
        .selectAll('*')
        .remove()
        .selectAll('rect')
        .remove()
    },

    updateRevisitTimeline() {
      this.updateTimeline('revisitTimeline')
    },

    updateGroundStationTimeline() {
      this.updateTimeline('groundStationTimeline')
    },

    updateTimeline(timelineId) {
      // TODO check display is UTC
      // TODO detail date and durration when over

      const intervals =
        timelineId == 'revisitTimeline'
          ? this.revisitTimelineIntervals[this.currentRevisitTimelineLabel]
          : this.groundStationTimelineIntervals[
              this.currentGroundStationTimelineLabel
            ]
      if (timelineId == 'revisitTimeline') {
        this.cleanRevisitTimelineView()
      } else {
        this.cleanGroundStationTimelineView()
      }

      const margin = 25
      const svgWidth = document.getElementById(timelineId).offsetWidth
      const svgHeight = 60
      let chartWidth = svgWidth - 2 * margin
      let chartHeight = 40

      let zoom = d3
        .zoom()
        .scaleExtent([0.5, 20]) // This control how much you can unzoom (x0.5) and zoom (x20)
        .extent([
          [0, 0],
          [chartWidth, chartHeight]
        ])
        .on('zoom', updateChart)

      let svg = d3
        .select('#' + timelineId)
        .append('svg')
        .attr('width', svgWidth)
        .attr('height', svgHeight)
        .call(zoom)

      let chart = svg.append('g').attr('transform', `translate(${margin}, 0)`)

      // zoom not used
      // this.brush = d3.brushX()
      //  .on('brush', this.setSelection)
      /*
      chart
        .append('g')
        .attr('class', 'brush')
        .call(this.brush)
        .call(this.brush.move, [0, 100])
        */
      let scaleX = d3
        .scaleTime()
        .range([0, chartWidth])
        .domain([this.startOfSimulationDate, this.endOfSimulationDate])

      let xAxis = svg
        .append('g')

        .attr('transform', `translate(0, ${chartHeight})`)
        .call(d3.axisBottom(scaleX))

      // create a tooltip
      const div = d3
        .select('#' + timelineId)
        .append('div')
        .attr('class', 'tooltip')
        .style('opacity', 0)
        .style('z-index', 1000)
        .style('border-radius', '4px')
        .style('background-color', 'black')
        .style('font', '12px')
        .style('color', 'white')
        .style('padding', '5px')
        .style('position', 'absolute')

      const self = this

      chart
        .selectAll('rect')
        .data(intervals)
        .enter()
        .append('rect')
        .attr('x', (interval) => {
          return scaleX(this.computeDateFromDuration(interval.start)) - margin
        })
        .attr('y', 0)
        .attr('width', (interval) => {
          let width =
            scaleX(this.computeDateFromDuration(interval.end)) -
            scaleX(this.computeDateFromDuration(interval.start))
          width = width > 0 ? width : 1
          return width
        })
        .attr('height', chartHeight)
        .style('fill', this.$vuetify.theme.dark ? 'white' : 'black')
        .on('mouseover', function (interval) {
          div.transition().duration(200).style('opacity', 0.9)
          div
            .html(
              'Length : ' +
                self.convertToHourFormat(interval.end - interval.start) +
                '<br>' +
                'Start: ' +
                self.computeDateFromDuration(interval.start) +
                '<br>' +
                'End: ' +
                self.computeDateFromDuration(interval.end)
            )
            .style('left', d3.event.pageX - 40 + 'px')
            .style('top', d3.event.pageY - 80 + 'px')
        })
        .on('mouseout', function (d) {
          div.transition().duration(200).style('opacity', 0)
          div.html('')
        })

      function updateChart() {
        // recover the new scale
        let newX = d3.event.transform.rescaleX(scaleX)

        // update axes with these new boundaries
        xAxis.call(d3.axisBottom(newX))

        chart
          .selectAll('rect')
          .data(intervals)
          .attr('x', function (interval) {
            return newX(self.computeDateFromDuration(interval.start)) - margin
          })
          .attr('width', (interval) => {
            let width =
              newX(self.computeDateFromDuration(interval.end)) -
              newX(self.computeDateFromDuration(interval.start))
            width = width > 0 ? width : 1

            return width
          })
      }
    },

    cleanViews() {
      this.cleanHeatmapView()
      this.cleanTimelines()
      this.cleanHistogramView()
      this.cleanGlobalHistogramView()
      this.cleanLocalHistogramView()
      return
    },

    prepareAndDisplayData() {
      if (this.buildingMap === true) {
        return
      }
      this.buildingMap = true

      // When switching page, purge plotly context to avoid memory leaks and errors when resizing page
      // No catch if the plot didn't rendered
      this.cleanViews()

      if (this.dataLoaded) {
        // prepare data
        this.updateDefaultMapFromCategory(this.appliedDashboardConfiguration)
        this.updateDefaultMapFromCategory(this.editedDashboardConfiguration)
        if (this.appliedDashboardConfiguration.currentMap == null) {
          this.buildingMap = false
          return
        }
        this.generateData()
        for (const data of this.currentData) {
          if (data.z.length === 0) {
            this.buildingMap = false
            this.emptyResult = true
            return
          }
        }
        this.generateGradient()
        // display containers
        if (this.emptyResult === false) {
          if (
            this.appliedDashboardConfiguration.displayTimelines ||
            this.appliedDashboardConfiguration.displayLocalHistogramView
          ) {
            this.displaySinglePointResultsFromLatLon()
          }
          if (
            this.appliedDashboardConfiguration.displayTimelines &&
            this.groundStationTimelineAvailable
          ) {
            if (!this.currentGroundStationName) {
              this.currentGroundStationName = this.groundStationNames[0]
            }
            this.displayGroundStationTimelineFromCache()
          }
          if (this.appliedDashboardConfiguration.displayHeatmapView === true) {
            this.drawHeatMap()
          }
          if (
            this.appliedDashboardConfiguration.displayHistogramView === true
          ) {
            this.drawHistogram()
          }
          if (
            this.appliedDashboardConfiguration.displayGlobalHistogramView ===
            true
          ) {
            this.drawGlobalHistogram()
          }
        }
      }
      this.heatMapHasUndefinedLocation = this.hasUndefinedLocationInHeatMap()
      this.buildingMap = false
    },

    generatePlotlyColorScale() {
      let gradient = this.heatMapCommons.currentGradient
      return gradient.map((val, i) => {
        return [
          constellationUtils.scale(i.toString(), 0, gradient.length - 1, 0, 1),
          val
        ]
      })
    },
    generatePlotlyHeatmapTicks() {
      let gradient = this.heatMapCommons.currentGradient
      this.heatMapCommons.gradientStep = 20
      this.heatMapCommons.tickValues = []
      this.heatMapCommons.tickText = []
      for (
        let i = 0;
        i < gradient.length / this.heatMapCommons.gradientStep;
        i++
      ) {
        let approximatedValue = constellationUtils.scale(
          i * this.heatMapCommons.gradientStep,
          0,
          gradient.length - 1,
          this.heatMapCommons.zMin,
          this.heatMapCommons.zMax
        )
        this.heatMapCommons.tickText.push(
          this.valueToUnitText(approximatedValue)
        )
        this.heatMapCommons.tickValues.push(approximatedValue.toFixed(1))
      }
    },
    drawHeatMap() {
      if (!this.currentData[this.heatmapMeshIndex]) {
        return
      }
      let xValues = this.currentData[this.heatmapMeshIndex].x
      let yValues = this.currentData[this.heatmapMeshIndex].y
      let zValues = this.currentData[this.heatmapMeshIndex].z
      const text = zValues.map((row, i) =>
        row.map((item, j) => {
          return `Longitude: ${dataTools.formatAngle(
            xValues[j]
          )}<br>Latitude: ${dataTools.formatAngle(
            yValues[i]
          )}<br>${this.getResultsFieldLabel(
            this.appliedDashboardConfiguration.currentMap
          )}: ${this.valueToUnitText(item)}`
        })
      )
      let data = [
        {
          x: xValues,
          y: yValues,
          z: zValues,
          type: 'heatmap',
          showscale: true,
          colorscale: this.heatMapCommons.colorscale,
          zmin: this.heatMapCommons.zMin,
          zmax: this.heatMapCommons.zMax,
          text: text,
          hoverinfo: 'text',
          zsmooth: 'best',
          connectgaps: false,
          colorbar: {
            tickmode: 'array',
            tickvals: this.heatMapCommons.tickValues,
            ticktext: this.heatMapCommons.tickText,
            tickfont: {
              color: this.getPlotTextColor
            }
          }
        }
      ]
      let layout = {
        xaxis: {
          title: {
            text: 'Longitude (deg)',
            font: {
              color: this.getPlotTextColor
            }
          },
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        yaxis: {
          title: {
            text: 'Latitude (deg)',
            font: {
              color: this.getPlotTextColor
            }
          },
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        plot_bgcolor: this.getPlotBackgroundColor,
        paper_bgcolor: this.getPlotBackgroundColor
      }
      try {
        Plotly.react(this.idWidgetHeatmapView, data, layout, {
          responsive: true
        })
      } catch (_) {}
    },

    hasUndefinedLocationInHeatMap() {
      if (!this.currentData[this.heatmapMeshIndex]) {
        return false
      }
      let xValues = this.currentData[this.heatmapMeshIndex].x
      let yValues = this.currentData[this.heatmapMeshIndex].y
      let zValues = this.currentData[this.heatmapMeshIndex].z
      for (let i = 0; i < yValues.length; i++) {
        for (let j = 0; j < xValues.length; j++) {
          if (zValues[i][j] === null) {
            return true
          }
        }
      }
      return false
    },

    drawHistogram() {
      let data = []
      let xData =
        this.statisticsHistogramMeshIndex === this.allMeshesIndex
          ? this.zFilteredNotNullAllMeshes
          : this.zFilteredNotNullPerMesh[this.statisticsHistogramMeshIndex]
      let dataRange = this.maxZInConfiguration - this.minZInConfiguration
      let xbinsStart = dataRange == 0 ? '' : this.minZInConfiguration
      let xbinsEnd = dataRange == 0 ? '' : this.maxZInConfiguration
      let rangeOfValues = xbinsEnd - xbinsStart

      let xbinsSize
      if (this.validHistogramInputs === true) {
        if (this.lastHistogramOptionUpdated == 'Bin size') {
          xbinsSize = this.appliedDashboardConfiguration.histogramBinSize
        } else {
          xbinsSize =
            dataRange /
            Math.ceil(
              this.appliedDashboardConfiguration.histogramNumberOfBins - 1
            )
        }
      } else {
        let totalNumberOfPoints = xData.length
        if (rangeOfValues === 0) {
          xbinsStart = this.minZInConfiguration - 0.5
          xbinsEnd = this.maxZInConfiguration + 0.5
        }
        let numberOfBins = Math.sqrt(totalNumberOfPoints)
        xbinsSize = Math.ceil(rangeOfValues / numberOfBins)
      }
      xbinsStart = xbinsStart - xbinsSize / 2
      xbinsEnd = xbinsEnd + xbinsSize / 2

      if (this.appliedDashboardConfiguration.displayOccurenceHistogram) {
        data.push({
          type: 'histogram',
          name: 'Occurence',
          histnorm: 'percent',
          xbins: {
            size: xbinsSize,
            start: xbinsStart,
            end: xbinsEnd
          },
          x: xData,
          hovertemplate: '%{y}<extra></extra>'
        })
      }
      if (this.appliedDashboardConfiguration.displayCumulativeHistogram) {
        data.push({
          type: 'histogram',
          name: 'Cumulative',
          histnorm: 'percent',
          cumulative: { enabled: true },
          xbins: {
            size: xbinsSize,
            start: xbinsStart,
            end: xbinsEnd
          },
          x: xData,
          xaxis: 'x2',
          yaxis: 'y2',
          hovertemplate: '%{y}<extra></extra>'
        })
      }
      this.computeStatisticsHistogramData(data)

      const xAxisText = this.appliedMetricName

      let nGraphs = data.length
      let layout = {
        grid: { rows: nGraphs, columns: 1, pattern: 'independent' },
        title: {
          text: 'Percentage (%) of mesh points',
          font: {
            color: this.getPlotTextColor,
            size: 20
          }
        },
        legend: {
          font: {
            color: this.getPlotTextColor
          }
        },
        xaxis: {
          title: {
            text:
              this.appliedDashboardConfiguration.displayOccurenceHistogram &&
              this.appliedDashboardConfiguration.displayCumulativeHistogram
                ? ''
                : xAxisText,
            font: {
              color: this.getPlotTextColor
            }
          },
          hoverformat: 'f',
          tickformat: 'f',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        xaxis2: {
          title: {
            text: xAxisText,
            font: {
              color: this.getPlotTextColor
            }
          },
          hoverformat: 'f',
          tickformat: 'f',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        yaxis: {
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        yaxis2: {
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        plot_bgcolor: this.getPlotBackgroundColor,
        paper_bgcolor: this.getPlotBackgroundColor
      }

      let self = this
      let config = {
        responsive: true,
        modeBarButtonsToAdd: [
          {
            name: 'Toggle bin configurations',
            icon: Plotly.Icons['pencil'],
            click: function () {
              self.displayHistogramOptions = !self.displayHistogramOptions
            }
          }
        ],
        modeBarButtonsToRemove: ['hoverClosestCartesian']
      }
      try {
        Plotly.react(this.idWidgetHistogramView, data, layout, config)

        let plot = document.getElementById(this.idWidgetHistogramView)
        this.correctPercentageToTotalNumberOfMeshPoints(
          this.idWidgetHistogramView
        )
        this.correctHoverUnits(plot)
        this.correctHoverPercentageToTotalNumberOfMeshPoints(plot)
        this.correctXAxisTextUnits(plot)
        Plotly.restyle(this.idWidgetHistogramView, {})
        Plotly.relayout(this.idWidgetHistogramView, {})
      } catch (_) {}
    },

    correctPercentageToTotalNumberOfMeshPoints(referencePlotId) {
      let totalNumberOfPoints =
        this.currentResultsLog.results[
          this.appliedDashboardConfiguration.currentMap
        ].length *
        this.currentResultsLog.results[
          this.appliedDashboardConfiguration.currentMap
        ][0].length
      const xData =
        this.statisticsHistogramMeshIndex === this.allMeshesIndex
          ? this.zFilteredNotNullAllMeshes
          : this.zFilteredNotNullPerMesh[this.statisticsHistogramMeshIndex]
      let numberOfPointsNotNull = xData.length

      Plotly.d3.selectAll('.ytick text').each(function (d, i) {
        let currentPlotId =
          Plotly.d3.select(this)[0][0].farthestViewportElement.parentNode
            .parentNode.parentNode.id
        if (currentPlotId === referencePlotId) {
          Plotly.d3
            .select(this)
            .html(
              (Plotly.d3.select(this).html() * numberOfPointsNotNull) /
                totalNumberOfPoints
            )
        }
      })
      Plotly.d3.selectAll('.y2tick text').each(function (d, i) {
        let currentPlotId =
          Plotly.d3.select(this)[0][0].farthestViewportElement.parentNode
            .parentNode.parentNode.id
        if (currentPlotId === referencePlotId) {
          Plotly.d3
            .select(this)
            .html(
              (Plotly.d3.select(this).html() * numberOfPointsNotNull) /
                totalNumberOfPoints
            )
        }
      })
    },

    correctHoverPercentageToTotalNumberOfMeshPoints(plotId) {
      let totalNumberOfPoints =
        this.currentResultsLog.results[
          this.appliedDashboardConfiguration.currentMap
        ].length *
        this.currentResultsLog.results[
          this.appliedDashboardConfiguration.currentMap
        ][0].length
      const xData =
        this.statisticsHistogramMeshIndex === this.allMeshesIndex
          ? this.zFilteredNotNullAllMeshes
          : this.zFilteredNotNullPerMesh[this.statisticsHistogramMeshIndex]
      let numberOfPointsNotNull = xData.length
      plotId.on('plotly_hover', function (data) {
        let old_hoverText = Plotly.d3.selectAll('.hovertext text.nums')
        let old_hoverPath = Plotly.d3.selectAll('.hovertext').selectAll('path')

        // if map or heatmap hover is currently being shown, we don't wanna interfere with it
        if (old_hoverText[0][0].textContent.includes('Longitude')) {
          old_hoverText[0].shift()
          old_hoverPath.shift()
        }
        // twice to consider if both the map and heatmap hovers are active
        if (old_hoverText[0][0].textContent.includes('Longitude')) {
          old_hoverText[0].shift()
          old_hoverPath.shift()
        }

        let new_hoverText =
          ' ' +
          (old_hoverText.html() * numberOfPointsNotNull) / totalNumberOfPoints

        //duplicate the hover text
        var attr = old_hoverText.node().attributes
        var attr_length = attr.length
        var node_name = old_hoverText.property('nodeName')
        var parent = Plotly.d3.select(old_hoverText.node().parentNode)
        var cloned = parent.append(node_name).attr('id', 'new_hovertext')
        var j = 0
        for (j = 0; j < attr_length; j += 1) {
          if (attr[j].nodeName !== 'id') {
            cloned.attr(attr[j].name, attr[j].value)
          }
        }
        let oldTextLength = old_hoverText[0][0].textContent.length
        let newTextLength = new_hoverText.length

        old_hoverPath
          .node()
          .setAttribute(
            'transform',
            'scale(' + newTextLength / oldTextLength + ',1)'
          )

        //assign the correct text to the next x-axis info text
        cloned.text(new_hoverText)
        cloned.style('opacity', 1)
        old_hoverText.style('opacity', 0)
      })
    },

    correctHoverUnits(plotId) {
      let self = this
      plotId.on('plotly_hover', function (data) {
        var old_infotext = Plotly.d3.selectAll('.axistext').selectAll('text')
        let valuesInRange = old_infotext[0][0].textContent.split('-')
        let new_infotext = self.valueToUnitText(
          valuesInRange[0].replace('−', '-')
        ) //plotly writes an 'em dash' for negative values, which is different from the minus sign
        if (valuesInRange.length == 2) {
          new_infotext +=
            ' - ' + self.valueToUnitText(valuesInRange[1].replace('−', '-')) //plotly writes an 'em dash' for negative values, which is different from the minus sign
        }

        //duplicate the x-axis info text
        var attr = old_infotext.node().attributes
        var attr_length = attr.length
        var node_name = old_infotext.property('nodeName')
        var parent = Plotly.d3.select(old_infotext.node().parentNode)
        var cloned = parent.append(node_name).attr('id', 'new_axistext')
        var j = 0
        for (j = 0; j < attr_length; j += 1) {
          if (attr[j].nodeName !== 'id') {
            cloned.attr(attr[j].name, attr[j].value)
          }
        }

        let oldTextLength = old_infotext[0][0].textContent.length
        let newTextLength = new_infotext.length

        Plotly.d3
          .selectAll('.axistext')
          .selectAll('path')
          .node()
          .setAttribute(
            'transform',
            'scale(' + newTextLength / oldTextLength + ',1)'
          )

        //assign the correct text to the next x-axis info text
        cloned.text(new_infotext)
        cloned.style('opacity', 1)
        old_infotext.style('opacity', 0)
      })
    },

    correctXAxisTextUnits(plotId) {
      let self = this
      let referencePlotId = plotId.id
      plotId.on('plotly_restyle', function () {
        Plotly.d3.selectAll('.xtick text').each(function (d, i) {
          let currentPlotId =
            Plotly.d3.select(this)[0][0].farthestViewportElement.parentNode
              .parentNode.parentNode.id
          if (currentPlotId === referencePlotId) {
            Plotly.d3
              .select(this)
              .html(Plotly.d3.select(this).html().replace('−', '-')) //plotly writes an 'em dash' for negative values, which is different from the minus sign
            if (isNaN(Number(Plotly.d3.select(this).html())) === false) {
              Plotly.d3
                .select(this)
                .html(self.valueToUnitText(Plotly.d3.select(this).html()))
            }
          }
        })
        Plotly.d3.selectAll('.x2tick text').each(function (d, i) {
          let currentPlotId =
            Plotly.d3.select(this)[0][0].farthestViewportElement.parentNode
              .parentNode.parentNode.id
          if (currentPlotId === referencePlotId) {
            Plotly.d3
              .select(this)
              .html(Plotly.d3.select(this).html().replace('−', '-')) //plotly writes an 'em dash' for negative values, which is different from the minus sign
            if (isNaN(Number(Plotly.d3.select(this).html())) === false) {
              Plotly.d3
                .select(this)
                .html(self.valueToUnitText(Plotly.d3.select(this).html()))
            }
          }
        })
      })
      plotId.on('plotly_relayout', function () {
        Plotly.d3.selectAll('.xtick text').each(function (d, i) {
          let currentPlotId =
            Plotly.d3.select(this)[0][0].farthestViewportElement.parentNode
              .parentNode.parentNode.id
          if (currentPlotId === referencePlotId) {
            Plotly.d3
              .select(this)
              .html(Plotly.d3.select(this).html().replace('−', '-')) //plotly writes an 'em dash' for negative values, which is different from the minus sign
            if (isNaN(Number(Plotly.d3.select(this).html())) === false) {
              Plotly.d3
                .select(this)
                .html(self.valueToUnitText(Plotly.d3.select(this).html()))
            }
          }
        })
        Plotly.d3.selectAll('.x2tick text').each(function (d, i) {
          let currentPlotId =
            Plotly.d3.select(this)[0][0].farthestViewportElement.parentNode
              .parentNode.parentNode.id
          if (currentPlotId === referencePlotId) {
            Plotly.d3
              .select(this)
              .html(Plotly.d3.select(this).html().replace('−', '-')) //plotly writes an 'em dash' for negative values, which is different from the minus sign
            if (isNaN(Number(Plotly.d3.select(this).html())) === false) {
              Plotly.d3
                .select(this)
                .html(self.valueToUnitText(Plotly.d3.select(this).html()))
            }
          }
        })
      })
    },

    drawLocalHistogram() {
      let histogramDataName =
        this.appliedDashboardConfiguration.currentCategory + 'GlobalHistogram'
      let histogramData = this.localHistogramDataToPlot
        ? this.generateHistogramDataFromResults(
            this.localHistogramDataToPlot[histogramDataName]
          )
        : null
      if (!histogramData) {
        return
      }

      let data = []
      if (this.appliedDashboardConfiguration.displayOccurenceHistogram) {
        data.push({
          type: 'bar',
          name: 'Occurence',
          x: histogramData.binMiddle,
          y: histogramData.binValue
        })
      }
      if (this.appliedDashboardConfiguration.displayCumulativeHistogram) {
        data.push({
          type: 'bar',
          name: 'Cumulative',
          x: histogramData.binMiddle,
          y: histogramData.binValueCumulative,
          xaxis: 'x2',
          yaxis: 'y2'
        })
      }
      this.localHistogramData = data

      let nGraphs = data.length
      let xAxisText = this.getResultsFieldLabel(
        this.appliedDashboardConfiguration.currentCategory
      )
      let layout = {
        bargap: 0.0,
        grid: { rows: nGraphs, columns: 1, pattern: 'independent' },
        title: {
          text:
            'Local ' +
            this.getResultsFieldLabel(
              this.appliedDashboardConfiguration.currentCategory
            ).toLowerCase() +
            ' histogram: lat ' +
            this.singlePointLatStr +
            ' / lon ' +
            this.singlePointLonStr,
          font: {
            color: this.getPlotTextColor,
            size: 20
          }
        },
        legend: {
          font: {
            color: this.getPlotTextColor
          }
        },
        xaxis: {
          title: {
            text:
              this.appliedDashboardConfiguration.displayOccurenceHistogram &&
              this.appliedDashboardConfiguration.displayCumulativeHistogram
                ? ''
                : xAxisText,
            font: {
              color: this.getPlotTextColor
            }
          },
          hoverformat: 'f',
          tickformat: 'f',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        xaxis2: {
          title: {
            text: xAxisText,
            font: {
              color: this.getPlotTextColor
            }
          },
          hoverformat: 'f',
          tickformat: 'f',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        yaxis: {
          title: {
            text: '% of total incidences',
            font: {
              color: this.getPlotTextColor
            }
          },
          tickformat: '%',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        yaxis2: {
          tickformat: '%',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        plot_bgcolor: this.getPlotBackgroundColor,
        paper_bgcolor: this.getPlotBackgroundColor
      }
      try {
        Plotly.react(this.idWidgetLocalHistogramView, data, layout, {
          responsive: true
        })
        let plot = document.getElementById(this.idWidgetLocalHistogramView)
        this.correctXAxisTextUnits(plot)
        this.correctHoverUnits(plot)
        Plotly.restyle(this.idWidgetLocalHistogramView, {})
        Plotly.relayout(this.idWidgetLocalHistogramView, {})
      } catch (_) {}
    },

    drawGlobalHistogram() {
      let histogramDataName =
        this.appliedDashboardConfiguration.currentCategory + 'GlobalHistogram'
      let histogramData = this.generateHistogramDataFromResults(
        this.currentResultsLog.results[histogramDataName]
      )

      let data = []
      if (this.appliedDashboardConfiguration.displayOccurenceHistogram) {
        data.push({
          type: 'bar',
          name: 'Occurence',
          x: histogramData.binMiddle,
          y: histogramData.binValue
        })
      }
      if (this.appliedDashboardConfiguration.displayCumulativeHistogram) {
        data.push({
          type: 'bar',
          name: 'Cumulative',
          x: histogramData.binMiddle,
          y: histogramData.binValueCumulative,
          xaxis: 'x2',
          yaxis: 'y2'
        })
      }
      this.globalHistogramData = data

      let nGraphs = data.length
      let xAxisText = this.getResultsFieldLabel(
        this.appliedDashboardConfiguration.currentCategory
      )
      let layout = {
        bargap: 0.0,
        grid: { rows: nGraphs, columns: 1, pattern: 'independent' },
        title: {
          text:
            'Global ' +
            this.getResultsFieldLabel(
              this.appliedDashboardConfiguration.currentCategory
            ).toLowerCase() +
            ' histogram',
          font: {
            color: this.getPlotTextColor,
            size: 20
          }
        },
        legend: {
          font: {
            color: this.getPlotTextColor
          }
        },
        xaxis: {
          title: {
            text:
              this.appliedDashboardConfiguration.displayOccurenceHistogram &&
              this.appliedDashboardConfiguration.displayCumulativeHistogram
                ? ''
                : xAxisText,
            font: {
              color: this.getPlotTextColor
            }
          },
          hoverformat: 'f',
          tickformat: 'f',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        xaxis2: {
          title: {
            text: xAxisText,
            font: {
              color: this.getPlotTextColor
            }
          },
          hoverformat: 'f',
          tickformat: 'f',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        yaxis: {
          title: {
            text: '% of total incidences',
            font: {
              color: this.getPlotTextColor
            }
          },
          tickformat: '%',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        yaxis2: {
          tickformat: '%',
          tickfont: {
            color: this.getPlotTextColor
          }
        },
        plot_bgcolor: this.getPlotBackgroundColor,
        paper_bgcolor: this.getPlotBackgroundColor
      }
      try {
        Plotly.react(this.idWidgetGlobalHistogramView, data, layout, {
          responsive: true
        })
        let plot = document.getElementById(this.idWidgetGlobalHistogramView)
        this.correctXAxisTextUnits(plot)
        this.correctHoverUnits(plot)
        Plotly.restyle(this.idWidgetGlobalHistogramView, {})
        Plotly.relayout(this.idWidgetGlobalHistogramView, {})
      } catch (_) {}
    },

    getResultFieldType(field) {
      if (resultsDescription[this.type][field] == null) {
        return ''
      }
      return resultsDescription[this.type][field].type
    },
    getResultsFieldLabel(field) {
      if (resultsDescription[this.type][field] == null) {
        return ''
      }
      return resultsDescription[this.type][field].label
    },
    getResultUnitLabel(field) {
      let fieldType = this.getResultFieldType(field)
      return (
        ' ' + dataTools.getUnitDisplayed(dataTools.getDefaultGuiUnit(fieldType))
      )
    },
    getMeshName(index) {
      return this.currentResultsLog?.results?.earthMeshes[index]?.name
        ? this.currentResultsLog?.results?.earthMeshes[index]?.name
        : index
    },
    computeResultToDisplay(fieldPath) {
      return this.computeRange(
        this.getResultFieldType(fieldPath[fieldPath.length - 1]),
        fieldPath,
        null
      )
    },
    computeRange(fieldType, fieldPath, results) {
      let currentResults = results
      if (currentResults == null) {
        if (
          this.currentMission == null ||
          this.currentResultsLog === null ||
          this.currentResultsLog.results == null ||
          this.currentResultsLog.results.ephemerides != null
        ) {
          return ''
        } else {
          currentResults = this.currentResultsLog.results
        }
      }
      let allVals = dataTools
        .getUniqueValues(currentResults, fieldPath)
        .filter((value) => !isNaN(value) && value != null)
      let min = dataTools.convertSi2Display(fieldType, Math.min(...allVals))
      let max = dataTools.convertSi2Display(fieldType, Math.max(...allVals))
      if (min === max) {
        return min.toString()
      } else {
        if (min.toString().includes('Infinity')) {
          return ''
        }
        return '[' + min + ' ... ' + max + ']'
      }
    },
    // TODO MFI copied from analytical mission, it would be better to reuse the code

    getMissionItem(mission) {
      if (mission && mission.constructor === Array) {
        return mission[0]
      } else {
        return mission
      }
    },

    isConstantLatitudeStep(earthMesh) {
      const lats = earthMesh.latitudes
      if (lats.length > 1) {
        return (
          Math.sin(lats[lats.length - 1]) - Math.sin(lats[lats.length - 2]) ==
          Math.sin(lats[1]) - Math.sin(lats[0])
        )
      } else {
        return true
      }
    },

    getStatisticalMetricName(currentMap) {
      if (currentMap.includes('forwardLinkTransferTime')) {
        return 'FORWARD_LINK_TRANSFER_TIME'
      }
      if (currentMap.includes('reverseLinkTransferTime')) {
        return 'REVERSE_LINK_TRANSFER_TIME'
      }
      if (currentMap.includes('timeBetweenReverseLinkArrivals')) {
        return 'TIME_BETWEEN_REVERSE_LINK_ARRIVALS'
      }
      if (currentMap.includes('pointNumberOfAccesses')) {
        return ''
      }
      if (currentMap.includes('pointAccessDuration')) {
        return 'ACCESS_DURATION'
      }
      if (currentMap.includes('pointRevisitTime')) {
        return 'REVISIT_TIME'
      }
      if (currentMap.includes('forwardToReverseLinkTime')) {
        return 'FORWARD_TO_REVERSE_LINK_TIME'
      }
      if (currentMap.includes('pointCollectTime')) {
        return 'COLLECT_TIME'
      }
      if (currentMap.includes('pointSimultaneousSpacecrafts')) {
        return 'SPACECRAFTS_SIMULTANEOUSLY_SEEN'
      }
    },

    getStatisticalMetricStatistic(currentMap) {
      if (currentMap.includes('Average')) {
        return 'average'
      }
      if (currentMap.includes('Median')) {
        return 'median'
      }
      if (currentMap.includes('Max')) {
        return 'maximum'
      }
      if (currentMap.includes('Min')) {
        return 'minimum'
      }
      if (currentMap.includes('Percentile')) {
        return 'percentiles'
      }
      if (currentMap.includes('Cumulative')) {
        return 'cumulative'
      }
    },

    // -------------------------------------------------------------------
    mergeString(fieldPath, results) {
      let currentResults = results
      if (currentResults == null) {
        if (
          this.currentMission == null ||
          this.currentResultsLog.results == null
        ) {
          return ''
        } else {
          currentResults = this.currentResultsLog.results
        }
      }
      let uniqueVals = dataTools.getUniqueValues(currentResults, fieldPath)
      if (uniqueVals.length === 1) {
        return uniqueVals[0]
      } else {
        return uniqueVals
      }
    },

    isStringInside(expected, fieldPath, results) {
      let currentResults = results
      if (currentResults == null) {
        if (
          this.currentMission == null ||
          this.currentResultsLog.results == null
        ) {
          return ''
        } else {
          currentResults = this.currentMission.results
        }
      }
      return (
        currentResults.filter(
          (element) => dataTools.getValue(element, fieldPath) === expected
        ).length > 0
      ) // TODO MFI stop on first would be better
    },

    getCurrentAPI() {
      if (this.currentMission) {
        return this.getAPI(this.currentMission.typeName + 's')
      }
    },

    getAPI(objectAPI) {
      return '/users/' + this.currentUser.id + '/' + objectAPI.toLowerCase()
    }
  }
}
</script>

<style scoped>
.cesiumColorScale {
  position: absolute;
  left: 1%;
  top: 1%;
  z-index: 2;
  color: #fff;
}

.bug-button {
  background: #ff5252 !important;
}
</style>
